import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createItem, deleteItem, fetchMatrix, updateItem } from './communicationMatrixApi';
import { ListResponse, CommunicationMatrixItem, CommunicationMatrixPayload, Pagination, ListParams } from './types';

interface MatrixState {
  items: CommunicationMatrixItem[];
  loading: boolean;
  error: string | null;
  pagination: Pagination;
  search: string;
  filterWho: string;
  filterToWhom: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
}

const initialState: MatrixState = {
  items: [],
  loading: false,
  error: null,
  pagination: { page: 1, pageSize: 10, total: 0, pages: 0 },
  search: '',
  filterWho: '',
  filterToWhom: '',
  sortBy: 'createdAt',
  sortDir: 'desc'
};

export const loadMatrix = createAsyncThunk<
  ListResponse,
  ListParams | undefined
>('matrix/load', async (params) => {
  const search = params?.search ?? '';
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 10;
  const who = params?.who ?? '';
  const toWhom = params?.toWhom ?? '';
  const sortBy = params?.sortBy ?? 'createdAt';
  const sortDir = params?.sortDir ?? 'desc';
  return fetchMatrix({ search, page, pageSize, who, toWhom, sortBy, sortDir });
});

export const createMatrix = createAsyncThunk<CommunicationMatrixItem, CommunicationMatrixPayload>(
  'matrix/create',
  async (payload) => createItem(payload)
);

export const updateMatrix = createAsyncThunk<CommunicationMatrixItem, { id: string; payload: CommunicationMatrixPayload }>(
  'matrix/update',
  async ({ id, payload }) => updateItem(id, payload)
);

export const deleteMatrix = createAsyncThunk<string, string>('matrix/delete', async (id) => {
  await deleteItem(id);
  return id;
});

const matrixSlice = createSlice({
  name: 'matrix',
  initialState,
  reducers: {
    setSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    },
    setFilterWho(state, action: PayloadAction<string>) {
      state.filterWho = action.payload;
    },
    setFilterToWhom(state, action: PayloadAction<string>) {
      state.filterToWhom = action.payload;
    },
    setSort(state, action: PayloadAction<{ sortBy: string; sortDir: 'asc' | 'desc' }>) {
      state.sortBy = action.payload.sortBy;
      state.sortDir = action.payload.sortDir;
    },
    setPage(state, action: PayloadAction<number>) {
      state.pagination.page = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pagination.pageSize = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMatrix.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMatrix.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(loadMatrix.rejected, (state) => {
        state.loading = false;
        state.error = 'No se pudo cargar la lista';
      })
      .addCase(createMatrix.fulfilled, (state, action) => {
        state.items = [action.payload, ...state.items];
        state.pagination.total += 1;
      })
      .addCase(updateMatrix.fulfilled, (state, action) => {
        state.items = state.items.map((item) => (item.id === action.payload.id ? action.payload : item));
      })
      .addCase(deleteMatrix.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      });
  }
});

export const { setSearch, setFilterWho, setFilterToWhom, setSort, setPage, setPageSize } = matrixSlice.actions;

export default matrixSlice.reducer;

